import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import {
  Folder,
  FolderPlus,
  FolderOpen,
  ChevronRight,
  ChevronDown,
  Edit,
  Trash2,
  MoreHorizontal,
  Home,
  Plus,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';

interface DocumentFolder {
  id: string;
  name: string;
  description?: string;
  color: string;
  icon: string;
  parent_folder_id?: string;
  path: string;
  depth: number;
  sort_order: number;
  is_active: boolean;
  document_count?: number;
}

interface FolderManagerProps {
  selectedFolderId: string | null;
  onSelectFolder: (folderId: string | null) => void;
  compact?: boolean;
}

export function FolderManager({
  selectedFolderId,
  onSelectFolder,
  compact = false,
}: FolderManagerProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<DocumentFolder | null>(null);
  const [parentFolderForNew, setParentFolderForNew] = useState<string | null>(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderColor, setNewFolderColor] = useState('#6b7280');

  // Fetch folders
  const { data: folders = [], isLoading } = useQuery({
    queryKey: ['document-folders'],
    queryFn: async () => {
      const { data, error } = await api
        .from('document_folders')
        .select('*, documents:legal_documents(count)')
        .eq('is_active', true)
        .order('sort_order')
        .order('name');

      if (error) throw error;

      return (data || []).map((f: any) => ({
        ...f,
        document_count: f.documents?.[0]?.count || 0,
      })) as DocumentFolder[];
    },
  });

  // Create folder mutation
  const createFolder = useMutation({
    mutationFn: async ({ name, parentId, color }: { name: string; parentId?: string | null; color: string }) => {
      const { error } = await api
        .from('document_folders')
        .insert({
          name,
          parent_folder_id: parentId || null,
          color,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-folders'] });
      setCreateDialogOpen(false);
      setNewFolderName('');
      setNewFolderColor('#6b7280');
      setParentFolderForNew(null);
      toast({ title: 'Carpeta creada', description: 'La carpeta se ha creado correctamente' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Update folder mutation
  const updateFolder = useMutation({
    mutationFn: async ({ id, name, color }: { id: string; name: string; color: string }) => {
      const { error } = await api
        .from('document_folders')
        .update({ name, color })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-folders'] });
      setEditingFolder(null);
      toast({ title: 'Carpeta actualizada' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Delete folder mutation
  const deleteFolder = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api
        .from('document_folders')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-folders'] });
      if (selectedFolderId) {
        onSelectFolder(null);
      }
      toast({ title: 'Carpeta eliminada' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Build folder tree
  const buildFolderTree = (parentId: string | null = null): DocumentFolder[] => {
    return folders
      .filter((f) => f.parent_folder_id === parentId)
      .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
  };

  const toggleExpand = (folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  const hasChildren = (folderId: string) => {
    return folders.some((f) => f.parent_folder_id === folderId);
  };

  const renderFolder = (folder: DocumentFolder, level: number = 0) => {
    const isExpanded = expandedFolders.has(folder.id);
    const isSelected = selectedFolderId === folder.id;
    const children = buildFolderTree(folder.id);
    const hasSubfolders = children.length > 0;

    return (
      <div key={folder.id}>
        <ContextMenu>
          <ContextMenuTrigger>
            <div
              className={cn(
                "flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors group",
                isSelected ? "bg-primary/10 text-primary" : "hover:bg-muted",
                level > 0 && "ml-4"
              )}
              onClick={() => onSelectFolder(folder.id)}
            >
              {hasSubfolders ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpand(folder.id);
                  }}
                  className="p-0.5 hover:bg-muted rounded"
                >
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
              ) : (
                <div className="w-5" />
              )}

              {isExpanded || isSelected ? (
                <FolderOpen className="w-4 h-4 shrink-0" style={{ color: folder.color }} />
              ) : (
                <Folder className="w-4 h-4 shrink-0" style={{ color: folder.color }} />
              )}

              <span className="flex-1 truncate text-sm">{folder.name}</span>

              {folder.document_count !== undefined && folder.document_count > 0 && (
                <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                  {folder.document_count}
                </span>
              )}

              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 opacity-0 group-hover:opacity-100"
                onClick={(e) => {
                  e.stopPropagation();
                  setParentFolderForNew(folder.id);
                  setCreateDialogOpen(true);
                }}
              >
                <Plus className="w-3 h-3" />
              </Button>
            </div>
          </ContextMenuTrigger>
          <ContextMenuContent>
            <ContextMenuItem onClick={() => {
              setParentFolderForNew(folder.id);
              setCreateDialogOpen(true);
            }}>
              <FolderPlus className="w-4 h-4 mr-2" />
              Nueva subcarpeta
            </ContextMenuItem>
            <ContextMenuItem onClick={() => {
              setEditingFolder(folder);
              setNewFolderName(folder.name);
              setNewFolderColor(folder.color);
            }}>
              <Edit className="w-4 h-4 mr-2" />
              Renombrar
            </ContextMenuItem>
            <ContextMenuSeparator />
            <ContextMenuItem
              onClick={() => {
                if (confirm(`¿Eliminar la carpeta "${folder.name}"?`)) {
                  deleteFolder.mutate(folder.id);
                }
              }}
              className="text-destructive"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Eliminar
            </ContextMenuItem>
          </ContextMenuContent>
        </ContextMenu>

        {isExpanded && hasSubfolders && (
          <div className="mt-0.5">
            {children.map((child) => renderFolder(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const rootFolders = buildFolderTree(null);

  return (
    <div className={cn("space-y-2", compact ? "p-2" : "p-4")}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm">Carpetas</h3>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={() => {
            setParentFolderForNew(null);
            setCreateDialogOpen(true);
          }}
        >
          <FolderPlus className="w-4 h-4" />
        </Button>
      </div>

      {/* Root folder (all documents) */}
      <div
        className={cn(
          "flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors",
          selectedFolderId === null ? "bg-primary/10 text-primary" : "hover:bg-muted"
        )}
        onClick={() => onSelectFolder(null)}
      >
        <Home className="w-4 h-4 shrink-0" />
        <span className="flex-1 text-sm">Todos los documentos</span>
      </div>

      {/* Folder tree */}
      <ScrollArea className={compact ? "h-[200px]" : "h-[400px]"}>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        ) : rootFolders.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            <Folder className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No hay carpetas</p>
            <Button
              variant="link"
              size="sm"
              onClick={() => setCreateDialogOpen(true)}
            >
              Crear primera carpeta
            </Button>
          </div>
        ) : (
          <div className="space-y-0.5">
            {rootFolders.map((folder) => renderFolder(folder))}
          </div>
        )}
      </ScrollArea>

      {/* Create/Edit Folder Dialog */}
      <Dialog
        open={createDialogOpen || !!editingFolder}
        onOpenChange={(open) => {
          if (!open) {
            setCreateDialogOpen(false);
            setEditingFolder(null);
            setNewFolderName('');
            setNewFolderColor('#6b7280');
            setParentFolderForNew(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingFolder ? 'Editar Carpeta' : 'Nueva Carpeta'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="folderName">Nombre de la carpeta</Label>
              <Input
                id="folderName"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Nombre de la carpeta"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="folderColor">Color</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  id="folderColor"
                  value={newFolderColor}
                  onChange={(e) => setNewFolderColor(e.target.value)}
                  className="w-10 h-10 rounded border cursor-pointer"
                />
                <div className="flex gap-2">
                  {['#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#8b5cf6', '#6b7280'].map((color) => (
                    <button
                      key={color}
                      className={cn(
                        "w-6 h-6 rounded-full border-2",
                        newFolderColor === color ? "border-foreground" : "border-transparent"
                      )}
                      style={{ backgroundColor: color }}
                      onClick={() => setNewFolderColor(color)}
                    />
                  ))}
                </div>
              </div>
            </div>

            {parentFolderForNew && !editingFolder && (
              <div className="text-sm text-muted-foreground">
                Se creará dentro de: <strong>{folders.find(f => f.id === parentFolderForNew)?.name}</strong>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCreateDialogOpen(false);
                setEditingFolder(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (!newFolderName.trim()) return;

                if (editingFolder) {
                  updateFolder.mutate({
                    id: editingFolder.id,
                    name: newFolderName,
                    color: newFolderColor,
                  });
                } else {
                  createFolder.mutate({
                    name: newFolderName,
                    parentId: parentFolderForNew,
                    color: newFolderColor,
                  });
                }
              }}
              disabled={!newFolderName.trim() || createFolder.isPending || updateFolder.isPending}
            >
              {editingFolder ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Export a simple folder selector for forms
interface FolderSelectorProps {
  value: string | null;
  onChange: (folderId: string | null) => void;
}

export function FolderSelector({ value, onChange }: FolderSelectorProps) {
  const { data: folders = [] } = useQuery({
    queryKey: ['document-folders'],
    queryFn: async () => {
      const { data, error } = await api
        .from('document_folders')
        .select('id, name, color, path, depth')
        .eq('is_active', true)
        .order('path');

      if (error) throw error;
      return data as DocumentFolder[];
    },
  });

  return (
    <div className="space-y-2">
      <Label>Carpeta</Label>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value || null)}
        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
      >
        <option value="">Sin carpeta (raíz)</option>
        {folders.map((folder) => (
          <option key={folder.id} value={folder.id}>
            {'  '.repeat(folder.depth)}{folder.name}
          </option>
        ))}
      </select>
    </div>
  );
}
